import React from 'react';
import { useNavigate } from 'react-router-dom';

// Layout & UI
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, StatusBadge, Table } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';

// Hooks & Auth
import { useProjectZones } from './hooks/useZones';
import { useAuth } from '@/core/auth/AuthContext';

// Utils
import { getZoneStatus, getZoneType, zoneStatusMap, zoneTypeMap } from '@/utils/status';

/**
 * ZoneListPage - Comprehensive list of all landscape zones across projects
 * Optimized for Landscape Management
 */
export default function ZoneListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    zones,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useProjectZones(companyId);

  return (
    <PageLayout
      title="Quản lý Khu vực (Zones)"
      description="Phân chia mặt bằng dự án thành các đơn vị không gian quản lý chuyên biệt cho thi công và bảo dưỡng."
      breadcrumbs={[{ label: 'Dự án' }, { label: 'Khu vực' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-map-2-line">Xem trên bản đồ</Button>
          <AuthButton
            module="projects"
            action="create"
            variant="primary"
            icon="ri-add-line"
            onClick={() => navigate('./create')}
          >
            Thêm khu vực
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tổng số khu vực"
            value={stats?.totalZones || 0}
            icon="ri-layout-grid-line"
            variant="primary"
          />
          <KPICard
            title="Tổng diện tích"
            value={Math.round(stats?.totalArea || 0)}
            icon="ri-ruler-2-line"
            variant="success"
            suffix=" m²"
          />
          <KPICard
            title="Sức khỏe trung bình"
            value={Math.round(stats?.avgHealth || 0)}
            icon="ri-heart-pulse-line"
            variant="warning"
            suffix="%"
          />
          <KPICard
            title="Công việc đang chạy"
            value={stats?.activeWorkOrders || 0}
            icon="ri-tools-line"
            variant="info"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã khu vực, tên khu vực..."
                value={filters.search}
                onSearch={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(zoneStatusMap).map(([val, cfg]) => ({ value: val, label: cfg.label }))
                    ]
                  },
                  {
                    id: 'type', label: 'Loại hình', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả loại' },
                      ...Object.entries(zoneTypeMap).map(([val, cfg]) => ({ value: val, label: cfg.label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <Table
            columns={[
              {
                key: 'code',
                title: 'Mã khu vực',
                headerAlign: 'center',
                align: 'center',
                className: 'w-[120px]'
              },
              {
                key: 'name',
                title: 'Tên khu vực & Phân loại',
                headerAlign: 'center',
                className: 'min-w-[250px]'
              },
              {
                key: 'area',
                title: 'Diện tích',
                headerAlign: 'center',
                align: 'center',
                className: 'w-[120px]'
              },
              {
                key: 'health',
                title: 'Sức khỏe',
                headerAlign: 'center',
                align: 'center',
                className: 'w-[140px]'
              },
              {
                key: 'status',
                title: 'Trạng thái',
                headerAlign: 'center',
                align: 'center',
                className: 'w-[150px]'
              },
              {
                key: 'project',
                title: 'Dự án',
                headerAlign: 'center',
                className: 'w-[180px]'
              },
            ]}
            data={zones}
            isLoading={isLoading}
            onRowClick={(item) => navigate(`./detail/${item.id}`)}
            renderCell={(item, key) => {
              switch (key) {
                case 'code':
                  return (
                    <span className="sm:text-sm text-xs font-normal text-os-primary tracking-tight">
                      {item.zone_code}
                    </span>
                  );
                case 'name':
                  const typeCfg = getZoneType(item.zone_type);
                  return (
                    <div className="flex flex-col">
                      <span className="sm:text-sm text-xs font-normal text-slate-900 line-clamp-1">{item.zone_name}</span>
                      <div className="flex items-center gap-1 mt-0.5 opacity-60">
                        <i className={`${typeCfg.icon} text-[10px]`} />
                        <span className="text-[10px] font-bold tracking-tighter">
                          {typeCfg.label}
                        </span>
                      </div>
                    </div>
                  );
                case 'area':
                  return (
                    <div className="flex items-center justify-center gap-1">
                      <span className="sm:text-sm text-xs font-normal text-slate-900">{item.area_m2?.toLocaleString() || '---'}</span>
                      <span className="text-[10px] font-black text-slate-400">m²</span>
                    </div>
                  );
                case 'health':
                  return (
                    <div className="flex flex-col items-center gap-1.5">
                      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${ item.health_score > 80 ? 'bg-emerald-500' : item.health_score > 50 ? 'bg-amber-500' : 'bg-red-500' }`}
                          style={{ width: `${item.health_score}%` }}
                        />
                      </div>
                      <span className="text-[10px] font-bold text-slate-400 tracking-tighter">
                        {item.health_score}% Sức khỏe
                      </span>
                    </div>
                  );
                case 'status':
                  return <StatusBadge status={item.status_code} type="zone" size="sm" />;
                case 'project':
                  return (
                    <div className="flex items-center gap-1.5">
                      <i className="ri-building-line text-slate-300" />
                      <span className="sm:text-sm text-xs font-normal text-slate-500 truncate max-w-[150px]">
                        {item.project?.name}
                      </span>
                    </div>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
